// Words for the sales replay, in plain SA English: run states, progress steps, what each
// check proves and what a failure means, and the report's list values.

import {
  type ReplayCheck,
  type ReplayCheckTally,
  type ReplayReport,
  type ReplayRunDetail,
  type ReplayScenario,
  reportDisagreements as disagreementCodes,
} from '@enp/domain/api';
import { type CheckStatus, sharePercent } from '@enp/domain/checks';
import { formatCount } from '@enp/domain/format';
import { addDays, daysBetween } from '@enp/domain/time';
import type { Status } from '@enp/ui';

export const RUN_STATUS: Record<ReplayRunDetail['status'], { label: string; status: Status }> = {
  queued: { label: 'Waiting to start', status: 'neutral' },
  snapshotting: { label: 'Reading sales', status: 'neutral' },
  simulating: { label: 'Replaying', status: 'neutral' },
  checking: { label: 'Checking', status: 'neutral' },
  // Neutral: "finished" says nothing about the result; the verdict badge beside it does
  // (review UI2A-1: a run with three failed checks must never look green).
  completed: { label: 'Finished', status: 'neutral' },
  failed: { label: 'Stopped with an error', status: 'danger' },
  cancelled: { label: 'Cancelled', status: 'neutral' },
};

export const CHECK_STATUS: Record<CheckStatus, { label: string; status: Status }> = {
  pass: { label: 'Pass', status: 'success' },
  partial: { label: 'Partial', status: 'warning' },
  fail: { label: 'Fail', status: 'danger' },
  could_not_run: { label: 'Could not run', status: 'neutral' },
};

/** The steps a run goes through, in order, with what each does. */
export const RUN_STEPS = [
  { key: 'snapshot', status: 'snapshotting', label: 'Read the sales from the source' },
  { key: 'simulate', status: 'simulating', label: 'Replay them in the practice branch' },
  { key: 'check', status: 'checking', label: 'Check the results' },
] as const;

/** What the run is doing right now, in words. */
export function progressText(
  progress: ReplayRunDetail['progress'],
  status: ReplayRunDetail['status'],
): string {
  const n = (v: number | null) => (v === null ? '' : formatCount(v));
  switch (progress.step) {
    case 'snapshot':
      return progress.done ? `${n(progress.done)} receipts read so far` : 'Asking the source…';
    case 'simulate':
      return progress.total
        ? `Replaying ${n(progress.total)} receipts: stock, production and sales`
        : 'Replaying the receipts';
    case 'write':
      return 'Writing the replayed tickets and stock to the practice branch';
    case 'ledger':
      return progress.total
        ? `Writing ${n(progress.done)} of ${n(progress.total)} practice stock movements`
        : 'Writing practice stock movements';
    case 'check':
      return 'Counting what was covered and checking VAT and stock';
    default:
      return status === 'queued' ? 'Waiting for a worker to pick it up' : 'Working…';
  }
}

/** Which step a run is on (0-based), from its status. */
export function stepIndex(status: ReplayRunDetail['status']): number {
  if (status === 'queued' || status === 'snapshotting') return 0;
  if (status === 'simulating') return 1;
  if (status === 'checking') return 2;
  return 3;
}

// ---------------------------------------------------------------- checks

const CHECK_TITLE: Record<string, string> = {
  'replay.coverage': 'Every receipt line processed and matched to a product',
  'replay.vat': 'Our VAT agrees with the source, line by line',
  'replay.stock': 'The simulated stock never ran short',
  'replay.ledger': 'Every simulated stock change recorded once',
};

export function checkTitle(name: string): string {
  return CHECK_TITLE[name] ?? name;
}

const plural = (n: number, one: string, many: string) =>
  `${formatCount(n)} ${n === 1 ? one : many}`;

/**
 * What a check's result means, plainly, with the next step. A strict failure always says
 * what failed and where to look.
 */
export function checkExplanation(
  check: ReplayCheck,
  report?: Pick<ReplayReport, 'vat'> & Partial<Pick<ReplayReport, 'stock'>>,
): string {
  const { name, result, expected, checked, failures } = check;
  if (result === 'could_not_run') {
    if (name === 'replay.vat') return 'No line matched a product, so there was no VAT to compare.';
    if (name === 'replay.stock') return 'No sale moved stock (no line matched a product).';
    if (name === 'replay.ledger') return 'No simulated stock changed, so nothing was recorded.';
    return 'There was nothing to check.';
  }
  if (result === 'partial') {
    if (name === 'replay.stock') {
      // Unmapped lines may have needed stock nobody could check (UI2A-202).
      const unchecked = expected - checked;
      return `${plural(unchecked, 'unmapped line', 'unmapped lines')} couldn’t be checked for stock; the ${plural(checked, 'line', 'lines')} checked never ran short. Map them in the catalogue import, then start a new replay.`;
    }
    return `Only ${formatCount(checked)} of ${formatCount(expected)} could be checked.`;
  }
  if (result === 'pass') {
    switch (name) {
      case 'replay.coverage':
        return `All ${plural(expected, 'line', 'lines')} processed and matched.`;
      case 'replay.vat':
        return `VAT agrees on all ${plural(checked, 'line', 'lines')} compared.`;
      case 'replay.stock':
        return 'Stock was there for every sale and every ingredient used, and none ran below zero.';
      case 'replay.ledger':
        return 'Every simulated stock change was recorded once.';
      default:
        return 'Passed.';
    }
  }
  switch (name) {
    case 'replay.coverage':
      return `${plural(failures, 'line', 'lines')} couldn’t be matched to a product — see the list. Map them in the catalogue import, then start a new replay for these dates.`;
    case 'replay.vat': {
      const errors = report?.vat.errors ?? 0;
      const differ = Math.max(0, failures - errors);
      const parts = [
        differ > 0 && `${plural(differ, 'line has', 'lines have')} different VAT from the source`,
        errors > 0 &&
          `${plural(errors, 'line’s', 'lines’')} VAT couldn’t be worked out (the list says why)`,
      ].filter(Boolean);
      return `${parts.join('; ')} — see the list. Usually a product’s VAT code (standard or zero-rated) needs fixing; then start a new replay.`;
    }
    case 'replay.stock': {
      // Three ways the simulated stock falls short (CR2-20): a sale took more than was
      // there, an ingredient was used beyond stock (production or made to order), or a
      // stock row ended below zero.
      const st = report?.stock;
      const parts = st
        ? [
            st.sold_beyond_stock > 0 &&
              `${plural(st.sold_beyond_stock, 'sale line', 'sale lines')} sold more than the simulated stock held`,
            st.consumed_beyond_stock > 0 &&
              `${plural(st.consumed_beyond_stock, 'ingredient use', 'ingredient uses')} (in production or made to order) took more than was in stock`,
            st.negative_rows > 0 &&
              `${plural(st.negative_rows, 'stock row', 'stock rows')} ended below zero`,
          ].filter(Boolean)
        : [];
      const what =
        parts.length > 0
          ? parts.join('; ')
          : `${plural(failures, 'stock shortfall', 'stock shortfalls')} in the simulation`;
      return `${what} — see Usage per product. Recipes, shelf lives or the production buffer may be off.`;
    }
    case 'replay.ledger':
      return `${plural(failures, 'simulated stock change was', 'simulated stock changes were')} already recorded with different figures, so they were left out: the sales or the catalogue changed since this scenario last ran, and its practice branch now mixes old and new. Start a new replay.`;
    default:
      return `${plural(failures, 'failure', 'failures')}.`;
  }
}

// ---------------------------------------------------------------- failures

/** What a stopped run offers: a new scenario, another try, or a trip to Imports first. */
export type RunFailureAction = 'new_scenario' | 'retry' | 'imports';

/** A failed run, in words, with the way out. */
export function runFailureText(run: Pick<ReplayRunDetail, 'error_code' | 'error'>): {
  title: string;
  message: string;
  action: RunFailureAction;
} {
  const code = run.error_code ?? '';
  switch (code) {
    case 'inputs_changed':
      return {
        title: 'Stopped: the sales or the catalogue changed',
        message:
          'Something changed since this replay last ran, so running it again would mix old and new figures in its practice branch. Nothing was written. Start a new replay for the same dates; this one stays as it was.',
        action: 'new_scenario',
      };
    case 'run_stalled':
      return {
        title: 'The replay stopped responding',
        message:
          'The worker running it stopped partway and nothing moved for a while, so it was ended. What it had written stays in the practice branch; running it again finishes the job without counting anything twice.',
        action: 'retry',
      };
    case 'cancelled':
      return {
        title: 'This replay was cancelled',
        message: 'It stopped before its report was ready. Run it again to get one.',
        action: 'retry',
      };
    case 'target_mismatch':
      return {
        title: 'The source is a different account now',
        message:
          'The connection answers as another account than the one confirmed. Check the connection under Imports.',
        action: 'imports',
      };
    case 'target_not_confirmed':
      return {
        title: 'The source account isn’t confirmed yet',
        message:
          'Confirm which account the connection reads under Imports, then run the replay again.',
        action: 'imports',
      };
    case 'connection_off':
      return {
        title: 'The connection to the source is switched off',
        message: 'Switch it back on under Imports, then run the replay again.',
        action: 'imports',
      };
    case 'connection_gone':
      return {
        title: 'The connection to the source was removed',
        message:
          'This replay can’t read its sales any more. Connect the source again under Imports and set up a new replay.',
        action: 'imports',
      };
    default:
      if (code.startsWith('source_')) {
        // The source's own error text is for support (the code is shown under it), not
        // for the person reading this (review UI2A-15).
        return {
          title: 'The source didn’t answer as expected',
          message:
            'Reading the sales from the source failed: it may be busy or briefly down. Nothing was written. Try again in a few minutes.',
          action: 'retry',
        };
      }
      return {
        title: 'The replay stopped with an error',
        message: run.error ?? 'Something went wrong while replaying. Try again.',
        action: 'retry',
      };
  }
}

/**
 * A run's state badge. A run that stopped because its inputs changed is a condition, not a
 * fault: "Stopped" in the warning tone (review UI2A-15).
 */
export function runStatusOf(run: Pick<ReplayRunDetail, 'status' | 'error_code'>): {
  label: string;
  status: Status;
} {
  if (run.status === 'failed' && run.error_code === 'inputs_changed') {
    return { label: 'Stopped', status: 'warning' };
  }
  return RUN_STATUS[run.status];
}

/** A stop that isn't a fault: warning tone, not danger (review UI2A-15). */
export function isConditionStop(run: Pick<ReplayRunDetail, 'status' | 'error_code'>): boolean {
  return run.status === 'cancelled' || run.error_code === 'inputs_changed';
}

/**
 * What a scenario's latest run leaves to do next (review CR2-22). A finished replay is a
 * record: fixing what it found changes the catalogue, so running it again would only be
 * refused (`inputs_changed`) — the way on is a new scenario. "Run again" stays for runs
 * that stopped without an answer (cancelled, stalled, a source or server error).
 */
export type ReplayNextStep = 'run' | 'run_again' | 'new_scenario' | 'none';

export function nextStep(
  run: Pick<ReplayRunDetail, 'status' | 'error_code' | 'error'> | null,
): ReplayNextStep {
  if (!run) return 'run';
  switch (run.status) {
    case 'completed':
      return 'new_scenario';
    case 'cancelled':
      return 'run_again';
    case 'failed': {
      const { action } = runFailureText(run);
      if (action === 'new_scenario') return 'new_scenario';
      return run.error_code === 'connection_gone' ? 'none' : 'run_again';
    }
    default:
      return 'none';
  }
}

/** A few words under a scenario's last-run badge when it stopped for a reason worth knowing. */
export function runFailureShort(
  run: Pick<ReplayRunDetail, 'status' | 'error_code'>,
): string | null {
  if (run.status !== 'failed') return null;
  switch (run.error_code) {
    case 'inputs_changed':
      return 'The sales or the catalogue changed since it ran';
    case 'run_stalled':
      return 'It stopped responding';
    case 'connection_gone':
      return 'The connection was removed';
    default:
      return null;
  }
}

// ---------------------------------------------------------------- honesty (UI2A-1…3)

/**
 * A finished run's verdict, from its checks: what the "Finished" badge doesn't say. When
 * the report's figures contradict its checks, that is the verdict (review UI2A-201).
 */
export function runVerdict(
  checks: ReplayCheckTally | null,
  figuresDisagree = false,
): { status: Status; label: string } | null {
  if (figuresDisagree) return { status: 'warning', label: 'Figures disagree' };
  if (!checks || checks.total === 0) return null;
  if (checks.failed > 0) {
    return { status: 'danger', label: `${plural(checks.failed, 'check', 'checks')} failed` };
  }
  if (checks.incomplete > 0) {
    return {
      status: 'warning',
      label: `${plural(checks.incomplete, 'check', 'checks')} incomplete`,
    };
  }
  return {
    status: 'success',
    label: checks.total === 1 ? 'The check passes' : `All ${formatCount(checks.total)} checks pass`,
  };
}

/**
 * The VAT tile (review UI2A-3, UI2A-208/210): the share of **all** lines whose VAT agrees
 * with the source, and two lines under it: what agrees, and what doesn't or wasn't
 * compared. The percentage is the domain's one rule (`sharePercent`, UI2A-301): rounded
 * down, 100% only when every line was compared and none differs or failed; the check row
 * states the same counts.
 */
export function vatFigure(vat: ReplayReport['vat']): {
  value: string | null;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  agree: string;
  compared: string;
} {
  const compared = vat.checked + vat.errors;
  const notCompared = Math.max(0, vat.lines - compared);
  const problems = vat.mismatched + vat.errors;
  if (compared === 0) {
    return {
      value: null,
      tone: 'neutral',
      agree: 'Nothing to compare: no line matched a product',
      compared: `0 of ${plural(vat.lines, 'line', 'lines')} compared`,
    };
  }
  const all = notCompared === 0 && problems === 0 && vat.matched === vat.lines;
  const pct = sharePercent(vat.matched, vat.lines) ?? 0;
  const rest = [
    vat.mismatched > 0 && `${formatCount(vat.mismatched)} differ`,
    vat.errors > 0 && `${formatCount(vat.errors)} couldn’t be worked out`,
    notCompared > 0 && `${formatCount(notCompared)} not compared`,
  ].filter(Boolean);
  return {
    value: `${pct}%`,
    tone: problems > 0 ? 'danger' : all ? 'success' : 'warning',
    agree:
      problems === 0
        ? all
          ? `All ${plural(vat.lines, 'line agrees', 'lines agree')}`
          : `All ${plural(compared, 'compared line agrees', 'compared lines agree')}`
        : `Agrees on ${formatCount(vat.matched)} of ${plural(vat.lines, 'line', 'lines')}`,
    compared: rest.length > 0 ? rest.join(' · ') : 'Every line compared',
  };
}

/** The three ways the simulated stock fell short, together (review UI2A-2). */
export function stockShortfalls(stock: ReplayReport['stock']): {
  total: number;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  parts: string[];
} {
  const total = stock.sold_beyond_stock + stock.consumed_beyond_stock + stock.negative_rows;
  const parts = [
    `${plural(stock.sold_beyond_stock, 'sale', 'sales')} beyond stock`,
    `${plural(stock.consumed_beyond_stock, 'ingredient use', 'ingredient uses')} beyond stock`,
    `${plural(stock.negative_rows, 'row', 'rows')} below zero`,
  ];
  return {
    total,
    tone:
      total > 0
        ? 'danger'
        : stock.sale_lines === 0
          ? 'neutral'
          : (stock.unmapped_lines ?? 0) > 0
            ? 'warning'
            : 'success',
    parts,
  };
}

/**
 * Where a report's figures and its checks disagree (review UI2A-2, UI2A-201), in words: a
 * check that passes while the figures show a problem. The headline and every verdict badge
 * say "Figures disagree" instead of "All checks pass". Should never happen; if it does, the
 * figures win. The server flags such runs with the same rule (`figures_disagree`).
 */
export function reportDisagreements(report: ReplayReport): string[] {
  const { coverage, vat, stock, written } = report;
  const short = stockShortfalls(stock);
  return disagreementCodes(report).map((code) => {
    switch (code) {
      case 'coverage':
        return `Every line is said to be matched, but ${plural(coverage.unmapped, 'line is', 'lines are')} unmapped.`;
      case 'vat':
        return `VAT is said to agree, but ${formatCount(vat.lines - vat.matched)} of ${formatCount(vat.lines)} lines differ or weren’t compared.`;
      case 'stock': {
        // Runs recorded before `unmapped_lines` existed have none.
        const unmapped = stock.unmapped_lines ?? 0;
        const found: [number, string][] = [
          [stock.sold_beyond_stock, short.parts[0] as string],
          [stock.consumed_beyond_stock, short.parts[1] as string],
          [stock.negative_rows, short.parts[2] as string],
          [unmapped, `${plural(unmapped, 'unmapped line', 'unmapped lines')} not checked`],
        ];
        const text = found.filter(([count]) => count > 0).map(([, t]) => t);
        return `Stock is said never to have run short, but: ${text.join(', ')}.`;
      }
      case 'ledger':
        return `Every stock change is said to be recorded once, but ${formatCount(written.conflicting_movements)} conflicted.`;
      case 'receipts':
        return `${formatCount(report.snapshot.receipts)} receipts were read but ${formatCount(report.sales.tickets)} replayed.`;
    }
  });
}

/**
 * The checks the figures dispute (review UI2A-303), by check name, each with the sentence
 * that says how: such a check shows "Disputed", never a green "Pass".
 */
export function disputedChecks(report: ReplayReport): Map<string, string> {
  const codes = disagreementCodes(report);
  const sentences = reportDisagreements(report);
  const out = new Map<string, string>();
  codes.forEach((code, i) => {
    // `receipts` is about the whole report, not one check.
    if (code !== 'receipts') out.set(`replay.${code}`, sentences[i] as string);
  });
  return out;
}

// ---------------------------------------------------------------- lists

export const VAT_STATUS_LABEL: Record<string, string> = {
  mismatch: 'Different',
  missing_ours: 'We have no VAT',
  missing_reported: 'Source sent no VAT',
  calculation_error: 'Couldn’t work it out',
};

export function vatStatusLabel(status: string): string {
  return VAT_STATUS_LABEL[status] ?? status.replace(/_/g, ' ');
}

export const UNMAPPED_REASON_LABEL: Record<string, string> = {
  no_mapping: 'No product linked to this item',
  unknown_channel: 'Sold in a store the import doesn’t know',
};

export const PRODUCT_CLASS_LABEL: Record<string, string> = {
  produced: 'Made ahead',
  made_to_order: 'Made to order',
  bought: 'Bought in',
};

// ---------------------------------------------------------------- scenarios

/** The default range: the last 12 whole weeks, ending yesterday. */
export function defaultRange(today: string): { from: string; to: string } {
  const to = addDays(today, -1);
  return { from: addDays(to, -(12 * 7 - 1)), to };
}

/** "84 days" / "12 weeks". */
export function rangeLength(from: string, to: string): string {
  const days = daysBetween(from, to) + 1;
  if (days % 7 === 0) return plural(days / 7, 'week', 'weeks');
  return plural(days, 'day', 'days');
}

/** "Buffer 10%", "3 days of bought-in stock". */
export function optionsSummary(options: ReplayScenario['options']): string {
  return `${options.production_buffer_pct}% production buffer · ${plural(
    options.opening_stock_days,
    'day',
    'days',
  )} of bought-in stock`;
}
